import { lightBlue, yellow, orange, red, grey } from "@mui/material/colors";
import { alpha } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";

import {
  leadUrgency,
  type SalesLeadHeat,
  type SalesOrgLeadCard,
} from "@xop/dashboard-shared";

import type { Tone } from "./quality.js";

/**
 * A single sx *style object* (or theme-callback) — i.e. `SxProps` minus
 * its array form. MUI's `sx={[a, b]}` array accepts this per element but
 * NOT the full `SxProps<Theme>` (which is itself recursively array-able),
 * so style-helper return types and `{ sx }` record fields must use this
 * narrower type to be usable as array members. Re-exported so the other
 * Sales modules share one definition instead of each re-deriving it.
 */
export type SxStyle = Exclude<SxProps<Theme>, ReadonlyArray<unknown>>;

/**
 * Heat-pill palette — the single source of truth for the lead "heat"
 * scale, shared by `LeadsTable` (Today + Leads list) and `LeadPage`
 * (detail hero) so they never drift.
 *
 * Thermal gradient — BLUE (cold) → YELLOW (warm) → ORANGE (hot) → RED
 * (on fire). Each level is visually unmistakable from its neighbours so
 * the heat column reads as a stacked colour scale at a glance. Yellow
 * gets dark text (yellow + white fails WCAG); the others stay white.
 *
 * Uses MUI's named-colour scales (no raw hex) so the converted pills
 * match the prior `bg-sky-600 text-white` etc. exactly.
 */
const HEAT_STYLES: Record<SalesLeadHeat, { label: string; sx: SxStyle }> = {
  cold:    { label: "cold",    sx: { bgcolor: lightBlue[700], color: "common.white" } },
  warm:    { label: "warm",    sx: { bgcolor: yellow[600],    color: grey[900] } },
  hot:     { label: "hot",     sx: { bgcolor: orange[800],    color: "common.white" } },
  on_fire: { label: "on fire", sx: { bgcolor: red[700],       color: "common.white" } },
};

export function heatPillSx(heat: SalesLeadHeat): { label: string; sx: SxStyle } {
  return HEAT_STYLES[heat] ?? HEAT_STYLES.cold;
}

/**
 * Heat ordering (cold < warm < hot < on fire) — the single source of truth for
 * "which heat is hotter", shared by the table's heat sort and by
 * {@link leadHeatDisplay}. Lives here next to the palette so the scale and its
 * order never drift.
 */
export const HEAT_RANK: Record<SalesLeadHeat, number> = {
  cold: 0,
  warm: 1,
  hot: 2,
  on_fire: 3,
};

/** What a lead's HEAT pill renders — a label + its colour sx, plus an optional
 * hover tooltip (set only for the urgency-elevated states). */
export interface HeatPillDisplay {
  label: string;
  sx: SxStyle;
  title?: string;
}

/**
 * What a lead's HEAT pill DISPLAYS, after its live deadline urgency is folded in.
 *
 * A lead can carry a cool agent `heat` (e.g. `warm`) yet have a buying window
 * closing within days — the urgency overlay ({@link leadUrgency}). That "act now"
 * pressure is the operator's headline, so it TAKES OVER the heat pill: a lead in
 * the super-hot tier reads **SUPER HOT** (orange), the louder last-chance tier
 * **LAST CHANCE** (red). The separate `UrgencyBadge` then carries only the action
 * ("Act within 12 days"), so the two surfaces split the old "Super hot · act
 * within 12 days" caption rather than repeating it. With no live urgency the pill
 * shows the agent's own heat (cold / warm / hot / on fire).
 *
 * Display-only — the agent's stored `heat` is untouched (the print/PDF export
 * still reports the raw value). `now` is injectable for deterministic tests.
 */
export function leadHeatDisplay(
  lead: Pick<
    SalesOrgLeadCard,
    "status" | "deadline" | "rfp_deadline" | "heat" | "deadline_verified"
  >,
  now: Date = new Date(),
): HeatPillDisplay {
  const { tier } = leadUrgency(lead, now);
  if (tier === "last_chance") {
    return {
      label: "last chance",
      sx: { bgcolor: red[700], color: "common.white" },
      title: "Last chance — this lead's window closes within 3 days. Act now.",
    };
  }
  if (tier === "super_hot") {
    return {
      label: "super hot",
      sx: { bgcolor: orange[800], color: "common.white" },
      title: "Super hot — this lead's window closes within 2 weeks. Act soon.",
    };
  }
  const base = heatPillSx(lead.heat);
  return { label: base.label, sx: base.sx };
}

/**
 * Resolve a quality {@link Tone} to a palette colour — the single source of
 * truth shared by the lead-detail **verdict badge** (`LeadPage`) and the Today
 * list's **Quality column** (`LeadsTable`) so the two surfaces never drift.
 */
export function tonePalette(t: Theme, tone: Tone): string {
  switch (tone) {
    case "good": return t.palette.success.main;
    case "warn": return t.palette.warning.main;
    case "bad":  return t.palette.error.main;
    default:     return t.palette.text.disabled;
  }
}

/**
 * A soft tinted chip — tone-coloured text on a 12%-alpha fill with a 40%-alpha
 * border. The verdict-badge look, factored out of `LeadPage` so the Today
 * Quality pill matches the lead-detail hero exactly. Returns an {@link SxStyle}
 * (array-member-safe) for `sx={[base, toneChipSx(tone)]}`.
 */
export function toneChipSx(tone: Tone): SxStyle {
  return {
    color: (t: Theme) => tonePalette(t, tone),
    bgcolor: (t: Theme) => alpha(tonePalette(t, tone), 0.12),
    borderColor: (t: Theme) => alpha(tonePalette(t, tone), 0.4),
  };
}
